const BACKTICK_FENCE_LINE_PATTERN = /^ {0,3}```/;
const TILDE_FENCE_LINE_PATTERN = /^ {0,3}~~~/;
const ATX_HEADING_LINE_PATTERN = /^ {0,3}#{1,6}(?:\s|$)/;
const SETEXT_UNDERLINE_LINE_PATTERN = /^ {0,3}(?:=+|-{2,})\s*$/;
const LIST_ITEM_LINE_PATTERN = /^ {0,3}(?:[-*+]\s|\d+[.)]\s)/;
const BLOCKQUOTE_LINE_PATTERN = /^ {0,3}>/;
const TABLE_ROW_LINE_PATTERN = /^ {0,3}\|/;
const TABLE_DELIMITER_LINE_PATTERN = /^ {0,3}\|? *:?-+:? *(?:\| *:?-+:? *)*\|? *$/;
const INDENTED_LINE_PATTERN = /^(?: {2,}|\t)/;
const CONTAINER_MARKER_PREFIX_PATTERN = /^(?:> ?)+/;

/** Below this many non-blank lines a detail is not worth hiding behind a control, so the answer
 * renders whole. A fenced block always collapses regardless of how few lines it spans. */
const MINIMUM_DETAIL_LINES = 3;

export interface AnalystAnswerSplit {
  headline: string;
  detail: string | null;
}

export interface AnalystAnswerSplitOptions {
  /** Keeps the whole opening section visible instead of only its first block. A report is written as
   * a summary followed by its supporting sections, so cutting at the first block boundary can leave
   * half the summary hidden. */
  anchorOnOpeningSection?: boolean;
}

type AnswerBlockKind = 'fence' | 'heading' | 'list' | 'blockquote' | 'table' | 'paragraph';

interface AnswerBlock {
  start: number;
  kind: AnswerBlockKind;
}

/** Splits a resolved analyst answer into an always-visible headline and a collapsible detail. The
 * headline is the first markdown block, where a fenced code block, a list, a blockquote and a table
 * each count as one indivisible block, so the split can never land between a table's header and its
 * delimiter row or between two items of the same list. Leading headings are absorbed until the
 * headline also contains something that is not a heading. There is no detail, and so no toggle, when
 * the answer is a single block, is nothing but headings, or is not worth collapsing. */
export function splitAnalystAnswerDetail(
  content: string,
  options: AnalystAnswerSplitOptions = {},
): AnalystAnswerSplit {
  const lines = content.split('\n');
  const scannedLines = lines.map(withoutCarriageReturn);
  const blocks = findBlocks(scannedLines);

  let headlineBlockCount = 0;
  while (headlineBlockCount < blocks.length) {
    headlineBlockCount++;
    if (blocks[headlineBlockCount - 1].kind !== 'heading') {
      break;
    }
  }

  if (options.anchorOnOpeningSection) {
    headlineBlockCount = Math.max(headlineBlockCount, openingSectionBlockCount(blocks, scannedLines));
  }

  if (headlineBlockCount >= blocks.length) {
    return { headline: content, detail: null };
  }

  const detailStart = blocks[headlineBlockCount].start;

  if (!isWorthCollapsing(blocks.slice(headlineBlockCount), scannedLines.slice(detailStart))) {
    return { headline: content, detail: null };
  }

  return {
    headline: lines.slice(0, detailStart).join('\n').trimEnd(),
    detail: lines.slice(detailStart).join('\n').trimEnd(),
  };
}

/** Blocks belonging to the opening section: its heading and everything under it up to the next
 * heading of the same or higher level. Matching on heading level rather than on the section's words
 * keeps this working whatever language the report is written in. Returns 0 when the content does not
 * open with a heading, which leaves the first-block rule in charge. */
function openingSectionBlockCount(blocks: AnswerBlock[], lines: string[]): number {
  if (!blocks.length || blocks[0].kind !== 'heading') {
    return 0;
  }

  const openingLevel = headingLevel(lines, blocks[0].start);

  for (let index = 1; index < blocks.length; index++) {
    if (blocks[index].kind !== 'heading') {
      continue;
    }

    if (headingLevel(lines, blocks[index].start) <= openingLevel) {
      return index;
    }
  }

  return blocks.length;
}

/** Heading depth, counting an underlined setext heading as the level its underline character means. */
function headingLevel(lines: string[], start: number): number {
  const atx = /^ {0,3}(#{1,6})/.exec(lines[start]);
  if (atx) {
    return atx[1].length;
  }

  return lines[start + 1]?.trimStart().startsWith('=') ? 1 : 2;
}

/** A table is never hidden — an answer shows its headline and any table it contains — so a detail
 * holding one is not collapsed at all, even when that leaves a long answer fully expanded. Otherwise
 * a detail is only worth a control when it carries a fenced block or enough lines to be a real
 * breakdown rather than one trailing remark. */
function isWorthCollapsing(detailBlocks: AnswerBlock[], detailLines: string[]): boolean {
  if (containsTable(detailLines)) {
    return false;
  }

  if (detailBlocks.some((block) => block.kind === 'fence')) {
    return true;
  }

  return detailLines.filter((line) => !isBlank(line)).length >= MINIMUM_DETAIL_LINES;
}

/** A table nested in a list item or a blockquote is still a table, and segmentation labels the whole
 * construct by its container, so the rule is enforced over the detail's lines rather than its block
 * kinds. Container markers and indentation come off first; fenced blocks are skipped whole so their
 * pipe lines are not read as rows. */
function containsTable(detailLines: string[]): boolean {
  const lines = detailLines.map(withoutContainerMarkers);
  let index = 0;

  while (index < lines.length) {
    if (isFence(lines[index])) {
      index = skipFencedBlock(lines, index);
      continue;
    }

    if (isTableStart(lines, index)) {
      return true;
    }

    index++;
  }

  return false;
}

function findBlocks(lines: string[]): AnswerBlock[] {
  const blocks: AnswerBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (isBlank(line)) {
      index++;
      continue;
    }

    const start = index;
    let kind: AnswerBlockKind = 'paragraph';

    if (isFence(line)) {
      kind = 'fence';
      index = skipFencedBlock(lines, index);
    } else if (ATX_HEADING_LINE_PATTERN.test(line)) {
      kind = 'heading';
      index++;
    } else if (LIST_ITEM_LINE_PATTERN.test(line)) {
      kind = 'list';
      index = skipListBlock(lines, index);
    } else if (BLOCKQUOTE_LINE_PATTERN.test(line)) {
      kind = 'blockquote';
      index = skipBlockquoteBlock(lines, index);
    } else if (isTableStart(lines, index)) {
      kind = 'table';
      index = skipTableBlock(lines, index);
    } else {
      const paragraph = skipParagraphBlock(lines, index);
      index = paragraph.end;
      kind = paragraph.isSetextHeading ? 'heading' : 'paragraph';
    }

    blocks.push({ start, kind });
  }

  return blocks;
}

/** An unterminated fence deliberately swallows the rest of the answer: splitting inside it would leave
 * an unclosed code block on both sides. */
function skipFencedBlock(lines: string[], start: number): number {
  const closingPattern = TILDE_FENCE_LINE_PATTERN.test(lines[start]) ? TILDE_FENCE_LINE_PATTERN : BACKTICK_FENCE_LINE_PATTERN;

  for (let index = start + 1; index < lines.length; index++) {
    if (closingPattern.test(lines[index])) {
      return index + 1;
    }
  }

  return lines.length;
}

/** Runs to the end of the list, not the end of its first item: subsequent items, their indented
 * continuation lines, lazy continuation text and the blank lines of a loose list all stay inside. */
function skipListBlock(lines: string[], start: number): number {
  let end = start + 1;
  let index = start + 1;

  while (index < lines.length) {
    const line = lines[index];

    if (isBlank(line)) {
      const next = findNextNonBlank(lines, index);
      if (next === null || !continuesList(lines[next])) {
        return end;
      }
      index = next;
      continue;
    }

    if (!continuesList(line) && startsNewBlock(lines, index)) {
      return end;
    }

    index++;
    end = index;
  }

  return end;
}

function continuesList(line: string): boolean {
  return LIST_ITEM_LINE_PATTERN.test(line) || INDENTED_LINE_PATTERN.test(line);
}

function skipBlockquoteBlock(lines: string[], start: number): number {
  let index = start + 1;

  while (index < lines.length && !isBlank(lines[index]) && (BLOCKQUOTE_LINE_PATTERN.test(lines[index]) || !startsNewBlock(lines, index))) {
    index++;
  }

  return index;
}

/** Rows carry no marker of their own once the delimiter row has been passed, so the block ends where
 * the renderer ends it: at a blank line or at the line that opens another kind of block. */
function skipTableBlock(lines: string[], start: number): number {
  let index = start + 1;

  while (index < lines.length && !isBlank(lines[index]) && !opensNonTableBlock(lines[index])) {
    index++;
  }

  return index;
}

/** A table row may be written with no leading pipe at all, so a table is recognised the way the
 * renderer recognises one — by its delimiter row — rather than by a leading `|`. Requiring a pipe on
 * both lines keeps a dashed setext underline from reading as a single-column delimiter. */
function isTableStart(lines: string[], index: number): boolean {
  if (TABLE_ROW_LINE_PATTERN.test(lines[index])) {
    return true;
  }

  const delimiter = index + 1 < lines.length ? lines[index + 1] : '';

  return lines[index].includes('|') && delimiter.includes('|') && TABLE_DELIMITER_LINE_PATTERN.test(delimiter);
}

function skipParagraphBlock(lines: string[], start: number): { end: number; isSetextHeading: boolean } {
  let index = start + 1;

  while (index < lines.length) {
    const line = lines[index];

    if (isBlank(line)) {
      break;
    }

    if (SETEXT_UNDERLINE_LINE_PATTERN.test(line)) {
      return { end: index + 1, isSetextHeading: true };
    }

    if (startsNewBlock(lines, index)) {
      break;
    }

    index++;
  }

  return { end: index, isSetextHeading: false };
}

function startsNewBlock(lines: string[], index: number): boolean {
  return opensNonTableBlock(lines[index]) || isTableStart(lines, index);
}

function opensNonTableBlock(line: string): boolean {
  return isFence(line) || ATX_HEADING_LINE_PATTERN.test(line) || LIST_ITEM_LINE_PATTERN.test(line) || BLOCKQUOTE_LINE_PATTERN.test(line);
}

function isFence(line: string): boolean {
  return BACKTICK_FENCE_LINE_PATTERN.test(line) || TILDE_FENCE_LINE_PATTERN.test(line);
}

function isBlank(line: string): boolean {
  return line.trim() === '';
}

/** Answers can arrive with `\r\n`, which would otherwise defeat every pattern anchored at the end of a
 * line. Only the scanned copy is stripped: the headline and the detail are sliced from the original so
 * the answer reaches the renderer exactly as it arrived. */
function withoutCarriageReturn(line: string): string {
  return line.endsWith('\r') ? line.slice(0, -1) : line;
}

function withoutContainerMarkers(line: string): string {
  return line.trimStart().replace(CONTAINER_MARKER_PREFIX_PATTERN, '').trimStart();
}

function findNextNonBlank(lines: string[], start: number): number | null {
  for (let index = start; index < lines.length; index++) {
    if (!isBlank(lines[index])) {
      return index;
    }
  }

  return null;
}
